<template>
  <div class="bc-collection__wrapper">
    <nav-btn-tab
      :id-nav-tab="'bc-main-tab'"
      :tabs="mainTabs"
      :cur-val="activeTab"
      @change-value="handleTabChange"
    />

    <!-- === КОЛЛЕКЦИЯ === -->
    <template v-if="activeTab === 'collection'">
      <system-card
        v-if="collSystemMessage"
        :system-message="collSystemMessage"
        :cb-func="loadCollection"
      />
      <div v-else-if="!isLoadingColl" class="bc-collection__list">
        <p v-if="items.length === 0" class="bc-collection__empty">
          {{ $t('businessCardCollection.empty') }}
        </p>
        <div
          v-for="item in items"
          :key="item.user.id"
          class="bc-collection__item"
          role="button"
          tabindex="0"
          @click="handleOpenCard(item.user.id)"
          @keydown.enter="handleOpenCard(item.user.id)"
        >
          <div class="bc-collection__avatar">
            <i class="exu-user" />
          </div>
          <div class="bc-collection__info">
            <span class="bc-collection__name">{{ item.user.name }}</span>
            <span
              v-if="item.user.position"
              class="bc-collection__position"
            >{{ item.user.position }}</span>
            <span
              v-if="item.user.company"
              class="bc-collection__company"
            >{{ item.user.company }}</span>
          </div>
          <i class="exu-chevron-right bc-collection__arrow" />
        </div>
      </div>
    </template>

    <!-- === ЗАПРОСЫ === -->
    <template v-else-if="activeTab === 'requests'">
      <drop-list
        :options="requestFilterOptions"
        :selected="requestsFilter"
        :default-name="$t('businessCardCollection.filter_incoming')"
        @update="handleFilterChange"
      />
      <div class="bc-req__list">
        <pre-loader v-if="isLoadingReq" />
        <system-card
          v-else-if="reqSystemMessage"
          :system-message="reqSystemMessage"
          :cb-func="loadRequests"
        />
        <template v-else>
          <p v-if="requests.length === 0" class="bc-collection__empty">
            {{ $t('businessCardCollection.empty_requests') }}
          </p>
          <div
            v-for="req in requests"
            :key="req.request_id"
            class="bc-req__item"
          >
            <div class="bc-req__avatar">
              <i class="exu-user" />
            </div>
            <div class="bc-req__body">
              <span class="bc-req__name">{{ req.user.name }}</span>
              <span class="bc-req__label">{{ requestLabel(req) }}</span>
              <div v-if="req.status === 'pending'" class="bc-req__actions">
                <template v-if="req.direction === 'incoming'">
                  <up-button
                    class="up-btn bc-req__btn bc-req__btn--accept"
                    :disabled="!!loadingActions[req.request_id]"
                    @click="handleAccept(req.request_id)"
                  >
                    {{ $t('businessCardCollection.btn_accept') }}
                  </up-button>
                  <up-button
                    class="up-btn bc-req__btn"
                    :disabled="!!loadingActions[req.request_id]"
                    @click="handleDecline(req.request_id)"
                  >
                    {{ $t('businessCardCollection.btn_decline') }}
                  </up-button>
                </template>
                <up-button
                  v-else
                  class="up-btn bc-req__btn bc-req__btn--revoke"
                  :disabled="!!loadingActions[req.request_id]"
                  @click="handleRevoke(req.request_id)"
                >
                  {{ $t('businessCardCollection.btn_revoke') }}
                </up-button>
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import appBaseNav from '@/components/mixins/appBaseNav';
import businessCard from '@/components/mixins/methodsBusinessCard';
import SystemCard from '@/components/shared/upCard/SystemCard.vue';

export default {
  name: 'BusinessCardCollection',
  components: { SystemCard },
  props: {
    locale: { type: String },
  },
  data() {
    return {
      activeTab: 'collection',

      isLoadingColl: true,
      collSystemMessage: null,
      items: [],

      requestsFilter: 'incoming',
      isLoadingReq: false,
      reqSystemMessage: null,
      requestsCache: { incoming: null, outgoing: null, declined: null },
      loadingActions: {},

      // Есть ли своя визитка. null — ещё не спрашивали, и до ответа принимать
      // обмен нельзя: он двусторонний, отдавать в него нечего.
      isCardCreated: null,
      ownCardRequest: null,
    };
  },
  computed: {
    mainTabs() {
      return [
        { name: this.$t('businessCardCollection.tab_collection'), value: 'collection' },
        { name: this.$t('businessCardCollection.tab_requests'), value: 'requests' },
      ];
    },
    requestFilterOptions() {
      return [
        { id: 'incoming', name: this.$t('businessCardCollection.filter_incoming') },
        { id: 'outgoing', name: this.$t('businessCardCollection.filter_outgoing') },
        { id: 'declined', name: this.$t('businessCardCollection.filter_declined') },
      ];
    },
    requests() {
      return this.requestsCache[this.requestsFilter] || [];
    },
  },
  mixins: [appBaseNav, businessCard],
  watch: {
    locale() {
      this.$store.commit('setPageTitle', this.$t('businessCardCollection.header_title'));
    },
  },
  mounted() {
    this.$store.commit('setPageTitle', this.$t('businessCardCollection.header_title'));
    this.loadCollection();
  },
  methods: {
    loadCollection() {
      this.isLoadingColl = true;
      this.collSystemMessage = null;
      this.getBusinessCardCollection()
        .then((data) => {
          this.isLoadingColl = false;
          this.items = data.items || [];
        })
        .catch(() => {
          this.isLoadingColl = false;
          this.collSystemMessage = this.$t('system.bad_connect');
        });
    },
    loadRequests() {
      // спрашиваем заранее, чтобы нажатие «Принять» не ждало ответа
      this.ensureOwnCard();
      const filter = this.requestsFilter;
      this.isLoadingReq = true;
      this.reqSystemMessage = null;
      const direction = filter === 'declined' ? null : filter;
      const status = filter === 'declined' ? 'declined' : 'pending';
      this.getBusinessCardRequests(direction, status)
        .then((data) => {
          this.isLoadingReq = false;
          this.requestsCache[filter] = data.items || [];
        })
        .catch(() => {
          this.isLoadingReq = false;
          this.reqSystemMessage = this.$t('system.bad_connect');
        });
    },
    handleTabChange(tab) {
      this.activeTab = tab;
      if (tab === 'requests' && this.requestsCache[this.requestsFilter] === null) {
        this.loadRequests();
      }
    },
    handleFilterChange(filter) {
      if (!filter) return;
      this.requestsFilter = filter;
      if (this.requestsCache[filter] === null) {
        this.loadRequests();
      }
    },
    handleOpenCard(userId) {
      this.$router.push({ name: 'businessCardCollectionItem', params: { userId } });
    },
    // Тексты общие с профилем участника: это один разговор об одном обмене,
    // и говорить о нём двумя наборами слов не стоит.
    toast(key, type) {
      this.$store.commit('pushToast', {
        text: this.$t(`participantProfile.exchange.${key}`),
        type,
      });
    },
    // При обрыве связи о причине уже сказал baseApi, и своё «не получилось»
    // рядом с ним — второе сообщение об одном и том же.
    toastFailure(error) {
      if (error && error.code === 'ERR_NETWORK') return;
      this.toast('toast_failed', 'danger');
    },
    // Признак своей визитки спрашиваем один раз за экран и держим обещание
    // запроса: нажать могут раньше, чем он вернётся, а до ответа считать, что
    // визитка есть, нельзя — иначе человек без визитки примет обмен.
    ensureOwnCard() {
      if (this.isCardCreated !== null) return Promise.resolve(this.isCardCreated === true);
      if (!this.ownCardRequest) {
        this.ownCardRequest = this.getMyBusinessCard()
          .then((data) => {
            this.isCardCreated = data.is_created === true;
            return this.isCardCreated;
          })
          .catch(() => {
            this.isCardCreated = false;
            return false;
          });
      }
      return this.ownCardRequest;
    },
    handleAccept(requestId) {
      // Обмен двусторонний: без своей визитки принимать нечего. Ведём её
      // создавать, а не отправляем accept. Отклонить при этом можно — отказ
      // ничего не отдаёт.
      this.ensureOwnCard().then((hasOwnCard) => {
        if (hasOwnCard) {
          this.acceptRequest(requestId);
          return;
        }
        this.toast('toast_need_card');
        this.$router.push({ name: 'businessCardEdit' });
      });
    },
    acceptRequest(requestId) {
      this.loadingActions[requestId] = true;
      this.acceptBusinessCardRequest(requestId)
        .then(() => {
          this.requestsCache.incoming = this.requestsCache.incoming.filter(
            (r) => r.request_id !== requestId,
          );
          this.toast('toast_exchanged');
        })
        // Раньше сбой гасился молча: запрос оставался в списке без всякого
        // объяснения, и это выглядело как «кнопка не работает».
        .catch((error) => this.toastFailure(error))
        .finally(() => {
          delete this.loadingActions[requestId];
        });
    },
    handleDecline(requestId) {
      this.loadingActions[requestId] = true;
      this.declineBusinessCardRequest(requestId)
        .then(() => {
          this.requestsCache.incoming = this.requestsCache.incoming.filter(
            (r) => r.request_id !== requestId,
          );
        })
        .catch((error) => this.toastFailure(error))
        .finally(() => {
          delete this.loadingActions[requestId];
        });
    },
    handleRevoke(requestId) {
      this.loadingActions[requestId] = true;
      this.revokeBusinessCardRequest(requestId)
        .then(() => {
          this.requestsCache.outgoing = this.requestsCache.outgoing.filter(
            (r) => r.request_id !== requestId,
          );
        })
        .catch((error) => this.toastFailure(error))
        .finally(() => {
          delete this.loadingActions[requestId];
        });
    },
    requestLabel(req) {
      if (req.status === 'pending') {
        return req.direction === 'incoming'
          ? this.$t('businessCardCollection.request_wants_exchange')
          : this.$t('businessCardCollection.request_sent');
      }
      return req.direction === 'incoming'
        ? this.$t('businessCardCollection.request_i_declined')
        : this.$t('businessCardCollection.request_they_declined');
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/style/variables.scss';

.bc-collection {
  &__wrapper {
    display: flex;
    flex-direction: column;
    max-width: 760px;
    margin: 0 auto;
  }

  &__list {
    display: flex;
    flex-direction: column;
    padding: 8px 12px 12px;
    gap: 8px;
  }

  &__empty {
    margin: 24px auto 0;
    font-size: 15px;
    color: #8a9ab0;
    text-align: center;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #fff;
    border-radius: 14px;
    padding: 14px 16px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:active {
      background: #f0f5fa;
    }
  }

  &__avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: $color-accent-soft;
    color: $color-accent-strong;
    font-size: 24px;
    flex-shrink: 0;
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: 15px;
    font-weight: 600;
    color: #1f1f1f;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__position {
    font-size: 13px;
    color: #4a6a8a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__company {
    font-size: 12px;
    color: #8a9ab0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__arrow {
    font-size: 18px;
    color: #c0ccd8;
    flex-shrink: 0;
  }
}

.bc-req {
  &__list {
    display: flex;
    flex-direction: column;
    padding: 8px 12px 12px;
    gap: 8px;
  }

  &__item {
    display: flex;
    gap: 12px;
    background: #fff;
    border-radius: 14px;
    padding: 14px 16px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07);
  }

  &__avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: $color-accent-soft;
    color: $color-accent-strong;
    font-size: 24px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: 15px;
    font-weight: 600;
    color: #1f1f1f;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__label {
    font-size: 13px;
    color: #8a9ab0;
  }

  &__actions {
    display: flex;
    gap: 8px;
    margin-top: 8px;
  }

  &__btn {
    flex: 1;
    height: 36px;
    border-radius: 10px;
    font-size: 14px;
    border-color: $color-primary;
    color: $color-primary;

    &--accept {
      background: $color-accent;
      color: #fff;
    }

    &--revoke {
      flex: 0 0 auto;
      padding: 0 16px;
    }
  }
}
</style>
